import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Show } from "@refinedev/mui";
import {
  IResourceComponentsProps,
  useShow,
  useTranslate,
  useList,
} from "@refinedev/core";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Grid,
  Avatar,
  Divider,
  alpha,
  IconButton,
  Paper,
  Skeleton,
  Button,
} from "@mui/material";
import {
  Person,
  LocationOn,
  MyLocation,
  Flag,
  AccessTime,
  Cancel,
  CheckCircle,
  Navigation,
  Refresh,
  ArrowBack,
  Place,
  DirectionsWalk,
  DirectionsCar,
  DirectionsBike,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { GeocodingService } from "../../services/geocoding.service";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { IJourney } from "../../types/events";
import { getRoutingProvider, Coordinates, calculateDistance, TransportMode } from "../../services/routing";

// Seuil de distance en mètres pour recalculer l'itinéraire
const ROUTE_RECALC_THRESHOLD_METERS = 50;

// Directions Component using routing service
const DirectionsPolyline: React.FC<{
  origin: Coordinates;
  destination: Coordinates;
  current?: Coordinates;
  mode: TransportMode;
}> = ({ origin, destination, current, mode }) => {
  const [routeCoordinates, setRouteCoordinates] = useState<Coordinates[]>([]);
  const [loading, setLoading] = useState(true);
  const map = useMap();

  // Ref pour stocker la dernière position et le mode utilisés pour le calcul
  const lastCalcPositionRef = useRef<Coordinates | null>(null);
  const lastModeRef = useRef<TransportMode | null>(null);
  const hasFittedBoundsRef = useRef(false);

  // Fonction pour vérifier si on doit recalculer
  const shouldRecalculate = useCallback((newCurrent?: Coordinates, newMode?: TransportMode): boolean => {
    // Toujours recalculer si le mode a changé
    if (newMode && lastModeRef.current !== newMode) return true;
    if (!newCurrent) return false;
    if (!lastCalcPositionRef.current) return true;

    const distance = calculateDistance(lastCalcPositionRef.current, newCurrent);
    return distance > ROUTE_RECALC_THRESHOLD_METERS;
  }, []);

  useEffect(() => {
    const fetchDirections = async () => {
      // Ne recalculer que si nécessaire (position changée de plus de 50m ou mode changé)
      if (routeCoordinates.length > 0 && !shouldRecalculate(current, mode)) {
        return;
      }

      try {
        setLoading(true);

        const routingProvider = getRoutingProvider();
        const result = await routingProvider.getRoute(origin, destination, { mode });

        setRouteCoordinates(result.coordinates);

        // Mémoriser la position et le mode actuels pour le prochain calcul
        if (current) {
          lastCalcPositionRef.current = current;
        }
        lastModeRef.current = mode;

        // Ajuster les bounds de la carte uniquement au premier chargement
        if (result.coordinates.length > 0 && !hasFittedBoundsRef.current) {
          const bounds = L.latLngBounds(result.coordinates);
          map.fitBounds(bounds, { padding: [50, 50] });
          hasFittedBoundsRef.current = true;
        }
      } catch (error) {
        console.error('[DirectionsPolyline] Error fetching directions:', error);
        // Fallback: utiliser une ligne droite si l'API échoue
        setRouteCoordinates([origin, destination]);
      } finally {
        setLoading(false);
      }
    };

    fetchDirections();
  }, [origin[0], origin[1], destination[0], destination[1], current?.[0], current?.[1], mode, map, shouldRecalculate]);

  if (loading && routeCoordinates.length === 0) {
    return null;
  }

  // Si current est fourni, trouver le point le plus proche sur le trajet
  let splitIndex = 0;
  if (current && routeCoordinates.length > 0) {
    let minDistance = Infinity;
    routeCoordinates.forEach((coord, index) => {
      const distance = calculateDistance(coord, current);
      if (distance < minDistance) {
        minDistance = distance;
        splitIndex = index;
      }
    });
  }

  return (
    <>
      {/* Tracé complet en bleu */}
      {routeCoordinates.length > 1 && (
        <Polyline
          positions={routeCoordinates}
          color="#2563eb"
          weight={5}
          opacity={0.8}
        />
      )}
    </>
  );
};

// Contrôle de mode de transport sur la carte
const TransportModeControl: React.FC<{
  mode: TransportMode;
  onModeChange: (mode: TransportMode) => void;
}> = ({ mode, onModeChange }) => {
  const modes: { value: TransportMode; icon: React.ReactNode; label: string }[] = [
    { value: 'walking', icon: <DirectionsWalk fontSize="small" />, label: 'À pied' },
    { value: 'driving', icon: <DirectionsCar fontSize="small" />, label: 'Voiture' },
    { value: 'cycling', icon: <DirectionsBike fontSize="small" />, label: 'Vélo' },
  ];

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 80,
        left: 10,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        bgcolor: 'white',
        borderRadius: 1,
        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
        overflow: 'hidden',
      }}
    >
      {modes.map((m) => (
        <IconButton
          key={m.value}
          size="small"
          onClick={() => onModeChange(m.value)}
          title={m.label}
          sx={{
            borderRadius: 0,
            bgcolor: mode === m.value ? 'primary.main' : 'white',
            color: mode === m.value ? 'white' : 'text.secondary',
            '&:hover': {
              bgcolor: mode === m.value ? 'primary.dark' : 'grey.100',
            },
          }}
        >
          {m.icon}
        </IconButton>
      ))}
    </Box>
  );
};

// Interface for user's places
interface IPlace {
  id: number;
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  radius: number;
  emoji?: string;
  type: string;
}

interface JourneyWithUser extends IJourney {
  user: {
    id: number;
    fullname: string;
    email: string;
    phone?: string;
    profilePicture?: string;
    entrepriseId?: number;
    entreprise?: {
      id: number;
      nom: string;
    };
  };
  friends?: Array<{
    id: number;
    fullname: string;
    email: string;
    profilePicture?: string;
  }>;
}

// Helper function to check if coordinates match a saved place
const findMatchingPlace = (
  lat: number,
  lng: number,
  places: IPlace[],
  toleranceMeters: number = 100
): IPlace | null => {
  if (!places || places.length === 0) return null;

  for (const place of places) {
    // Calculate distance using Haversine formula
    const R = 6371000; // Earth radius in meters
    const dLat = ((place.latitude - lat) * Math.PI) / 180;
    const dLng = ((place.longitude - lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat * Math.PI) / 180) *
        Math.cos((place.latitude * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    // Use the place's radius or default tolerance
    const effectiveRadius = Math.max(place.radius || toleranceMeters, toleranceMeters);
    if (distance <= effectiveRadius) {
      return place;
    }
  }
  return null;
};

// Component to display address with place detection
const AddressDisplay: React.FC<{
  latitude: number;
  longitude: number;
  places: IPlace[];
  label: string;
  color: string;
  icon: React.ReactNode;
}> = ({ latitude, longitude, places, label, color, icon }) => {
  const [address, setAddress] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  const matchingPlace = useMemo(
    () => findMatchingPlace(latitude, longitude, places),
    [latitude, longitude, places]
  );

  useEffect(() => {
    if (matchingPlace) {
      setAddress(matchingPlace.address || matchingPlace.name);
      setLoading(false);
      return;
    }

    const geocodingService = GeocodingService.getInstance();
    geocodingService
      .getAddress(latitude, longitude)
      .then((addr) => {
        setAddress(addr);
        setLoading(false);
      })
      .catch(() => {
        setAddress(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
        setLoading(false);
      });
  }, [latitude, longitude, matchingPlace]);

  return (
    <Paper sx={{ p: 2, bgcolor: alpha(color, 0.1), height: '100%' }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="caption" fontWeight={600} sx={{ color }}>
            {label}
          </Typography>
        </Stack>

        {matchingPlace ? (
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600}>
              {matchingPlace.emoji} {matchingPlace.name}
            </Typography>
            {matchingPlace.address && (
              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <Place sx={{ fontSize: 14, mt: 0.2, color: theme.palette.text.secondary }} />
                <Typography variant="caption" color="text.secondary">
                  {matchingPlace.address}
                </Typography>
              </Stack>
            )}
          </Stack>
        ) : loading ? (
          <Skeleton variant="text" width="80%" />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {address}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

export const JourneyShow: React.FC<IResourceComponentsProps> = () => {
  const { id } = useParams<{ id: string }>();
  const translate = useTranslate();
  const theme = useTheme();
  const navigate = useNavigate();

  // État pour le mode de transport
  const [transportMode, setTransportMode] = useState<TransportMode>('walking');

  // Add pulse animation for live tracking badge
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { queryResult } = useShow<JourneyWithUser>({
    resource: "journeys",
    id,
    meta: {
      join: [
        { field: "user" },
        { field: "user.entreprise" },
        { field: "friends" },
      ],
    },
    queryOptions: {
      // Auto-refresh every 5 seconds for active journeys
      refetchInterval: (data): number | false => {
        const journey = data?.data;
        // Only poll if journey is active (not ended)
        if (journey && !journey.hasEnded) {
          return 5000; // 5 seconds
        }
        return false; // Don't poll if journey has ended
      },
      refetchIntervalInBackground: false,
    },
  });

  const { data, isLoading } = queryResult;
  const journey = data?.data;

  // Fetch user's places to detect if origin/destination matches a saved place
  const { data: placesData } = useList<IPlace>({
    resource: "places",
    filters: journey?.userId
      ? [{ field: "userId", operator: "eq", value: journey.userId }]
      : [],
    pagination: { mode: "off" },
    queryOptions: {
      enabled: !!journey?.userId,
    },
  });

  const userPlaces = useMemo(() => placesData?.data || [], [placesData]);

  // Custom markers for the map
  const originIcon = L.icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  // Avatar marker pour la position actuelle de l'utilisateur
  const createAvatarIcon = (profilePicture?: string, fullname?: string) => {
    const initials = fullname ? fullname.charAt(0).toUpperCase() : '?';
    const avatarContent = profilePicture
      ? `<img src="${profilePicture}" alt="avatar" style="width: 100%; height: 100%; object-fit: cover;" />`
      : `<span style="color: white; font-weight: bold; font-size: 16px;">${initials}</span>`;

    return L.divIcon({
      className: 'custom-avatar-marker',
      html: `
        <div style="
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 3px solid #2563eb;
          background: ${profilePicture ? 'transparent' : '#2563eb'};
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          ${avatarContent}
        </div>
        <div style="
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 10px solid #2563eb;
          margin: -2px auto 0;
        "></div>
      `,
      iconSize: [40, 52],
      iconAnchor: [20, 52],
      popupAnchor: [0, -52],
    });
  };

  const currentIcon = useMemo(
    () => createAvatarIcon(journey?.user?.profilePicture, journey?.user?.fullname),
    [journey?.user?.profilePicture, journey?.user?.fullname]
  );

  const destinationIcon = L.icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  if (isLoading || !journey) {
    return <Show isLoading={isLoading} />;
  }

  const formatCoordinates = (lat: number, lng: number) => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance.toFixed(2);
  };

  const totalDistance = calculateDistance(
    journey.originLatitude,
    journey.originLongitude,
    journey.destinationLatitude,
    journey.destinationLongitude
  );

  const traveledDistance = calculateDistance(
    journey.originLatitude,
    journey.originLongitude,
    journey.currentLatitude,
    journey.currentLongitude
  );

  const remainingDistance = calculateDistance(
    journey.currentLatitude,
    journey.currentLongitude,
    journey.destinationLatitude,
    journey.destinationLongitude
  );

  const progressPercentage = Math.min(
    100,
    Math.max(0, (parseFloat(traveledDistance) / parseFloat(totalDistance)) * 100)
  );

  return (
    <Box>
      {/* Custom header with back button only */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={() => navigate("/history")}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2),
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          {translate("journeys.details.title")}
        </Typography>
      </Box>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={3}>
          {/* Informations */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Person color="primary" />
                      <Typography variant="h6" fontWeight={600}>
                        {translate("journeys.details.information")}
                      </Typography>
                    </Stack>
                    <Chip
                      label={journey.hasEnded ? translate("journeys.status.ended") : translate("journeys.status.active")}
                      color={journey.hasEnded ? "default" : "success"}
                      icon={journey.hasEnded ? <Cancel /> : <CheckCircle />}
                      size="small"
                    />
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {translate("journeys.details.id")}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      #{journey.id}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={journey.user?.profilePicture}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'primary.main',
                      }}
                    >
                      {(journey.user?.fullname || journey.user?.email)?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {journey.user?.fullname || journey.user?.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {journey.user?.email}
                      </Typography>
                      {journey.user?.phone && (
                        <Typography variant="caption" color="text.secondary">
                          {journey.user?.phone}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Journey Progress */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Navigation color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      {translate("journeys.progress.title")}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.progress.total")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {totalDistance} km
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.progress.traveled")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {traveledDistance} km
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.progress.remaining")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="warning.main">
                        {remainingDistance} km
                      </Typography>
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <Stack direction="row" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" color="text.secondary">
                          {translate("journeys.progress.completion")}
                        </Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {progressPercentage.toFixed(1)}%
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          width: '100%',
                          height: 8,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${progressPercentage}%`,
                            height: '100%',
                            bgcolor: 'success.main',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Interactive Map */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                      {translate("journeys.locations.title")}
                    </Typography>
                    {!journey.hasEnded && (
                      <Chip
                        icon={<Refresh />}
                        label={translate("journeys.map.liveTracking")}
                        color="success"
                        size="small"
                        sx={{ animation: 'pulse 2s ease-in-out infinite' }}
                      />
                    )}
                  </Stack>
                  <Divider />

                  {/* Map Container */}
                  <Box sx={{ height: 500, width: '100%', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                    {/* Contrôles de mode de transport - Only for active journeys */}
                    {!journey.hasEnded && (
                      <TransportModeControl mode={transportMode} onModeChange={setTransportMode} />
                    )}
                    <MapContainer
                      center={[journey.currentLatitude, journey.currentLongitude]}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                      key={`${journey.currentLatitude}-${journey.currentLongitude}`}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url={`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_API_KEY}`}
                        minZoom={0}
                        maxZoom={20}
                      />

                      {/* Origin Marker (Green) */}
                      <Marker
                        position={[journey.originLatitude, journey.originLongitude]}
                        icon={originIcon}
                      >
                        <Popup>
                          <Stack spacing={1}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              🟢 {translate("journeys.locations.origin")}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {formatCoordinates(journey.originLatitude, journey.originLongitude)}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                window.open(
                                  `https://www.google.com/maps?q=${journey.originLatitude},${journey.originLongitude}`,
                                  "_blank"
                                )
                              }
                            >
                              Google Maps
                            </Button>
                          </Stack>
                        </Popup>
                      </Marker>

                      {/* Current Position Marker (Blue) */}
                      <Marker
                        position={[journey.currentLatitude, journey.currentLongitude]}
                        icon={currentIcon}
                      >
                        <Popup>
                          <Stack spacing={1}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              🔵 {translate("journeys.locations.current")}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {formatCoordinates(journey.currentLatitude, journey.currentLongitude)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {journey.lastHeartbeat
                                ? `Last update: ${new Date(journey.lastHeartbeat).toLocaleTimeString()}`
                                : "No recent update"}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                window.open(
                                  `https://www.google.com/maps?q=${journey.currentLatitude},${journey.currentLongitude}`,
                                  "_blank"
                                )
                              }
                            >
                              Google Maps
                            </Button>
                          </Stack>
                        </Popup>
                      </Marker>

                      {/* Destination Marker (Red) */}
                      <Marker
                        position={[journey.destinationLatitude, journey.destinationLongitude]}
                        icon={destinationIcon}
                      >
                        <Popup>
                          <Stack spacing={1}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              🔴 {translate("journeys.locations.destination")}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {formatCoordinates(journey.destinationLatitude, journey.destinationLongitude)}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                window.open(
                                  `https://www.google.com/maps?q=${journey.destinationLatitude},${journey.destinationLongitude}`,
                                  "_blank"
                                )
                              }
                            >
                              Google Maps
                            </Button>
                          </Stack>
                        </Popup>
                      </Marker>

                      {/* Google Directions Route - Only for active journeys */}
                      {!journey.hasEnded && (
                        <DirectionsPolyline
                          origin={[journey.originLatitude, journey.originLongitude]}
                          destination={[journey.destinationLatitude, journey.destinationLongitude]}
                          current={[journey.currentLatitude, journey.currentLongitude]}
                          mode={transportMode}
                        />
                      )}
                    </MapContainer>
                  </Box>

                  {/* Addresses with Place Detection */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <AddressDisplay
                        latitude={journey.originLatitude}
                        longitude={journey.originLongitude}
                        places={userPlaces}
                        label={translate("journeys.locations.origin")}
                        color={theme.palette.success.main}
                        icon={<MyLocation sx={{ color: 'success.main', fontSize: 20 }} />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <AddressDisplay
                        latitude={journey.currentLatitude}
                        longitude={journey.currentLongitude}
                        places={userPlaces}
                        label={translate("journeys.locations.current")}
                        color={theme.palette.primary.main}
                        icon={<LocationOn sx={{ color: 'primary.main', fontSize: 20 }} />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <AddressDisplay
                        latitude={journey.destinationLatitude}
                        longitude={journey.destinationLongitude}
                        places={userPlaces}
                        label={translate("journeys.locations.destination")}
                        color={theme.palette.error.main}
                        icon={<Flag sx={{ color: 'error.main', fontSize: 20 }} />}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Timestamps */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTime color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      {translate("journeys.timestamps.title")}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.timestamps.started")}
                      </Typography>
                      <Typography variant="body2">
                        {new Date(journey.createdAt).toLocaleString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.timestamps.lastUpdate")}
                      </Typography>
                      <Typography variant="body2">
                        {journey.lastHeartbeat
                          ? new Date(journey.lastHeartbeat).toLocaleString()
                          : "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.timestamps.updated")}
                      </Typography>
                      <Typography variant="body2">
                        {new Date(journey.updatedAt).toLocaleString()}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Shared With */}
          {journey.friends && journey.friends.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={600}>
                      {translate("journeys.sharedWith.title")} ({journey.friends.length})
                    </Typography>
                    <Divider />
                    <Stack spacing={1}>
                      {journey.friends.map((friend) => (
                        <Stack key={friend.id} direction="row" spacing={2} alignItems="center">
                          <Avatar
                            src={friend.profilePicture}
                            sx={{ width: 32, height: 32 }}
                          >
                            {(friend.fullname || friend.email)?.[0]?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {friend.fullname || friend.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {friend.email}
                            </Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};
